import {
  flex,
  width,
  maxWidth,
  PageHeader,
  pv,
  ph,
} from '@openeducation/oe-it-components';
import { useAsyncStorage } from '@openeducation/oe-it-hooks';
import { useNotificationContext } from '@openeducation/oe-it-providers';
import {
  GenericNotification,
  LiveClassNotification,
  LiveClassTeacherReminderNotification,
} from '@openeducation/oe-it-shared-types';
import { styled } from '@openeducation/oe-it-themes';
import * as React from 'react';
import { Platform, View } from 'react-native';

import { Banner } from '../../atoms/banner/banner';
import { useClassEmpty } from '../../hooks/use-class-empty';
import { useJoinClass } from '../../hooks/use-join-class';
import { usePageLinks } from '../../hooks/use-page-links';
import type { PortalPaths } from '../../interfaces/portal-paths.enum';
import { EmptyClassModal } from '../../molecules/empty-class-modal/empty-class-modal';
import { NotificationClassBanner } from '../../molecules/notification-class-banner/notification-class-banner';
import { NotificationGenericBanner } from '../../molecules/notification-generic-banner/notification-generic-banner';

export interface PortalLayoutProps {
  title?: string;
  children?: React.ReactNode;
  selectedPage?: PortalPaths;
}

export const PortalLayout: React.FC<PortalLayoutProps> = ({
  title,
  children,
  selectedPage,
}) => {
  const [hideDisclaimer, setHideDisclaimer] =
    useAsyncStorage('portal.disclaimer');
  const { pages } = usePageLinks(selectedPage);

  const {
    cleanNotificationClicked,
    notificationClicked,
    genericNotifications,
    lcNotifications,
    lcTeacherReminderNotifications,
    classEmpty,
  } = useNotificationContext();

  const { onConfirmClassEmpty, onCancelClassEmpty, isClassEmptyVisible } =
    useClassEmpty(classEmpty);

  React.useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }

    if (title) {
      document.title = `${title} - Teacher Portal - Open English`;
    }
  }, [title]);

  const { joinClass, joinClassUrl } = useJoinClass({
    url: notificationClicked,
  });

  React.useEffect(() => {
    if (joinClassUrl) {
      joinClass();
      cleanNotificationClicked();
    }
  }, [cleanNotificationClicked, joinClass, joinClassUrl]);

  return (
    <Container>
      <StickyHeader>
        <StyledPageHeader pages={pages} />
        {lcNotifications?.map((notification: LiveClassNotification) => (
          <NotificationClassBanner
            key={notification.id}
            notification={notification}
          />
        ))}
        {genericNotifications?.map((notification: GenericNotification) => (
          <NotificationGenericBanner
            key={notification.id}
            notification={notification}
          />
        ))}
        {lcTeacherReminderNotifications?.map(
          (notification: LiveClassTeacherReminderNotification) => (
            <NotificationClassBanner
              key={notification.id}
              notification={notification}
            />
          )
        )}
        {!hideDisclaimer && (
          <Banner
            title="Disclaimer"
            message={`Feel free to answer student questions and provide feedback in the Ask A Teacher section at any time. Your input is very valuable to us, but please note that we will be unable to offer compensation for your time unless you're responding during your scheduled hours or get approval from the Teacher Administrator. Thank you for enriching the student-teacher bond!`}
            onDismiss={() => setHideDisclaimer(true)}
            type="error"
          />
        )}
      </StickyHeader>
      <View
        style={[
          maxWidth(1600),
          pv(32),
          ph(32),
          width('100%'),
          flex('align-self-center'),
        ]}
      >
        {children}
      </View>
      <EmptyClassModal
        onCancelClass={onCancelClassEmpty}
        onConfirmClass={onConfirmClassEmpty}
        isVisible={isClassEmptyVisible}
      />
    </Container>
  );
};

const StyledPageHeader = styled(PageHeader).attrs(({ theme }) => ({
  containerStyle: {
    shadowColor: theme.color.transparent,
    borderBottomWidth: 1,
    borderBottomColor: theme.color.lightPrimary,
  },
}))``;

const Container = styled.View`
  height: 100%;
  background-color: ${({ theme }) => theme.color.lightPrimary};
`;

const StickyHeader = styled.View`
  position: ${() => (Platform.OS === 'web' ? 'sticky' : 'relative')};
  top: 0;
  z-index: 10;
`;
